// Pannello "Supporto" nella vista admin globale. Visibile solo a super_admin
// (la pagina admin viene mostrata solo se isSuperAdmin=true, vedi AdminView).
// Lista filtrata dei ticket + dialog dettaglio con thread + reply + status
// switcher + debug snapshot. Realtime su support_messages per nuovi reply
// senza dover ricaricare.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LifeBuoy, Search, MessageSquare, Copy, Loader2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Textarea } from "@v2/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@v2/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@v2/components/ui/tabs";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { currentIntlLocale } from "../../lib/i18n";
import { copyToClipboard } from "../../lib/share";

type Status = "open" | "in_progress" | "waiting_user" | "resolved";

type TicketRow = {
  id: string;
  user_id: string | null;
  team_id: string | null;
  title: string;
  category: string;
  status: Status;
  debug_snapshot: any;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  user_email: string | null;
  user_name: string | null;
  team_name: string | null;
  team_slug: string | null;
  messages_count: number;
  last_message_at: string | null;
  last_message_body: string | null;
  last_message_is_admin: boolean | null;
};

type Message = {
  id: string;
  ticket_id: string;
  author_user_id: string | null;
  is_admin: boolean;
  body: string;
  created_at: string;
};

type Filter = "all" | "open" | "in_progress" | "waiting_user" | "resolved";

export function SupportAdminSection() {
  const { t } = useTranslation("support");
  const [filter, setFilter] = useState<Filter>("open");
  const [search, setSearch] = useState("");
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);

  const ticketsQuery = useQuery({
    queryKey: ["admin-support-tickets"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("support_tickets_admin")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []) as TicketRow[];
    },
    refetchInterval: 30_000,
  });

  // useMemo per stabilizzare il reference (data || []) e non far ricalcolare
  // `filtered` a ogni render quando il fetch e' in cache.
  const tickets = useMemo(() => ticketsQuery.data || [], [ticketsQuery.data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets
      .filter((t) => filter === "all" || t.status === filter)
      .filter((t) => {
        if (!q) return true;
        return (
          t.title.toLowerCase().includes(q) ||
          (t.user_email || "").toLowerCase().includes(q) ||
          (t.team_name || "").toLowerCase().includes(q) ||
          (t.team_slug || "").toLowerCase().includes(q)
        );
      });
  }, [tickets, filter, search]);

  const openCount = tickets.filter((t) => t.status === "open").length;
  const inProgressCount = tickets.filter((t) => t.status === "in_progress").length;
  const waitingCount = tickets.filter((t) => t.status === "waiting_user").length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-primary" />
          {t("admin.title")}
          {openCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 text-xs font-bold">
              {openCount}
            </span>
          )}
        </CardTitle>
        <CardDescription>{t("admin.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="all">{t("admin.filterAll")} <span className="ml-1 opacity-60 text-xs">{tickets.length}</span></TabsTrigger>
            <TabsTrigger value="open">{t("admin.filterOpen")} <span className="ml-1 opacity-60 text-xs">{openCount}</span></TabsTrigger>
            <TabsTrigger value="in_progress">{t("admin.filterInProgress")} <span className="ml-1 opacity-60 text-xs">{inProgressCount}</span></TabsTrigger>
            <TabsTrigger value="waiting_user">{t("admin.filterWaitingUser")} <span className="ml-1 opacity-60 text-xs">{waitingCount}</span></TabsTrigger>
            <TabsTrigger value="resolved">{t("admin.filterResolved")}</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("admin.searchPh")} className="pl-9" />
        </div>

        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">{t("admin.empty")}</p>
        ) : (
          <ul className="space-y-1.5">
            {filtered.map((tk) => (
              <li key={tk.id}>
                <button
                  onClick={() => setOpenTicketId(tk.id)}
                  className="w-full flex items-start gap-3 px-3 py-2.5 rounded-2xl bg-background hover:bg-muted/50 text-left transition-colors"
                >
                  <StatusDot status={tk.status} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{tk.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {tk.user_email || tk.user_id?.slice(0,8) || "—"}
                      {tk.team_name && <> · {tk.team_name}</>}
                      <> · {tk.category}</>
                    </p>
                    {tk.last_message_body && (
                      <p className="text-xs text-muted-foreground/80 italic truncate mt-0.5">
                        {tk.last_message_is_admin ? "↗ " : "↘ "}
                        {tk.last_message_body}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {new Date(tk.last_message_at || tk.updated_at).toLocaleString(currentIntlLocale(), {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                      <MessageSquare className="h-3 w-3" />{tk.messages_count}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <TicketDialog
        ticketId={openTicketId}
        onClose={() => {
          setOpenTicketId(null);
          ticketsQuery.refetch();
        }}
      />
    </Card>
  );
}

function StatusDot({ status }: { status: Status }) {
  const color =
    status === "open" ? "bg-amber-500" :
    status === "in_progress" ? "bg-sky-500" :
    status === "waiting_user" ? "bg-violet-500" :
    "bg-emerald-500";
  return <span className={cn("eb-dot mt-1.5", color)} aria-hidden />;
}

function TicketDialog({ ticketId, onClose }: { ticketId: string | null; onClose: () => void }) {
  const { t } = useTranslation("support");
  const queryClient = useQueryClient();
  const open = !!ticketId;
  const [reply, setReply] = useState("");

  const ticketQuery = useQuery({
    queryKey: ["admin-support-ticket", ticketId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("support_tickets_admin")
        .select("*")
        .eq("id", ticketId)
        .maybeSingle();
      if (error) throw error;
      return data as TicketRow | null;
    },
    enabled: open,
  });
  const messagesQuery = useQuery({
    queryKey: ["admin-support-messages", ticketId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("support_messages")
        .select("id, ticket_id, author_user_id, is_admin, body, created_at")
        .eq("ticket_id", ticketId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as Message[];
    },
    enabled: open,
    refetchInterval: open ? 15_000 : false,
  });

  const sendReply = useMutation({
    mutationFn: async () => {
      if (!ticketId) throw new Error("no ticket");
      const { data: u } = await supabaseV2.auth.getUser();
      if (!u.user) throw new Error("no user");
      const { error } = await supabaseV2.from("support_messages").insert({
        ticket_id: ticketId,
        author_user_id: u.user.id,
        is_admin: true,
        body: reply.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setReply("");
      queryClient.invalidateQueries({ queryKey: ["admin-support-messages", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["admin-support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
    },
  });

  const setStatus = useMutation({
    mutationFn: async (next: Status) => {
      if (!ticketId) return;
      const { error } = await supabaseV2
        .from("support_tickets")
        .update({
          status: next,
          resolved_at: next === "resolved" ? new Date().toISOString() : null,
        })
        .eq("id", ticketId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
    },
  });

  const ticket = ticketQuery.data;
  const messages = messagesQuery.data || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        {ticket && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LifeBuoy className="h-5 w-5 text-primary" />
                <span className="truncate">{ticket.title}</span>
              </DialogTitle>
              <DialogDescription className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                <Field label={t("admin.fromLabel")} value={`${ticket.user_name || "—"} · ${ticket.user_email || ticket.user_id?.slice(0,8) || "—"}`} />
                <Field label={t("admin.teamLabel")} value={ticket.team_name || "—"} />
                <Field label={t("admin.categoryLabel")} value={ticket.category} />
                <Field label={t("admin.statusLabel")} value={t(`status.${ticket.status}`)} />
              </DialogDescription>
            </DialogHeader>

            <div className="grid lg:grid-cols-[1fr_280px] gap-3">
              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}
              </div>
              <aside className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">{t("admin.debugLabel")}</p>
                {ticket.debug_snapshot ? (
                  <pre className="text-xs font-mono bg-slate-950 text-slate-200 rounded-md p-2 max-h-[40vh] overflow-auto">
                    {JSON.stringify(ticket.debug_snapshot, null, 2)}
                  </pre>
                ) : (
                  <p className="text-xs text-muted-foreground italic">{t("admin.debugEmpty")}</p>
                )}
                <div className="flex flex-col gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(ticket.id)}>
                    <Copy className="h-3 w-3" /> {t("admin.copyId")}
                  </Button>
                  {ticket.user_email && (
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(ticket.user_email!)}>
                      <Copy className="h-3 w-3" /> {t("admin.copyEmail")}
                    </Button>
                  )}
                </div>
              </aside>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); if (reply.trim()) sendReply.mutate(); }}
              className="space-y-2"
            >
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={t("admin.replyPh")}
                rows={3}
                maxLength={5000}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" disabled={!reply.trim() || sendReply.isPending}>
                  {sendReply.isPending ? <><Loader2 className="h-4 w-4 animate-spin" />{t("admin.replying")}</> : t("admin.reply")}
                </Button>
                <span className="flex-1" />
                {ticket.status !== "in_progress" && ticket.status !== "resolved" && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setStatus.mutate("in_progress")}>{t("admin.markInProgress")}</Button>
                )}
                {ticket.status !== "waiting_user" && ticket.status !== "resolved" && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setStatus.mutate("waiting_user")}>{t("admin.markWaitingUser")}</Button>
                )}
                {ticket.status !== "resolved" ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => setStatus.mutate("resolved")}>{t("admin.markResolved")}</Button>
                ) : (
                  <Button type="button" size="sm" variant="outline" onClick={() => setStatus.mutate("open")}>{t("admin.reopen")}</Button>
                )}
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground font-semibold">{label}</p>
      <p className="text-sm text-foreground truncate">{value}</p>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const { t } = useTranslation("support");
  const when = new Date(message.created_at).toLocaleString(currentIntlLocale(), {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className={cn("rounded-lg p-3 text-sm border",
      message.is_admin ? "bg-violet-50 border-violet-200" : "bg-card border-border"
    )}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-semibold text-muted-foreground">
          {message.is_admin ? t("thread.adminLabel") : t("thread.youLabel")}
        </span>
        <span className="text-xs text-muted-foreground">{when}</span>
      </div>
      <p className="whitespace-pre-wrap break-words">{message.body}</p>
    </div>
  );
}
